import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import { ArrowLeft, Award, CheckCircle, Edit, PlusSquare } from 'lucide-react';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import CertificateForm from '../certificate/CertificateForm';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function CertificateSection({ data, loading, onBack, toast, onSuccess }) {
    const [modalCertificate, setModalCertificate] = useState(false);

    function handleToggle() {
        if (data) {
            setModalCertificate({ type: 'editCertificate', item: data });
        } else {
            setModalCertificate(true);
        }
    }

    function handleSuccess(item) {
        setModalCertificate(false);
        if (item === 'createCertificate') {
            toast.success('Certificado criado com sucesso!', { position: 'top-center' });
        }
        if (item === 'editCertificate') {
            toast.success('Certificado atualizado com sucesso!', { position: 'top-center' });
        }
        onSuccess();
    }

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={panelAccent(colors.secondaryPurple)} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <Award size={20} color={panelAccent(colors.occasionalPurple)} />
                        </IconBadge>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>Geração de certificados</SectionTitle>
                                {data && <StatusPill><CheckCircle size={12} /> Configurado</StatusPill>}
                            </TitleRow>
                            <SectionDescription>
                                Crie e personalize certificados para os participantes do seu evento.
                            </SectionDescription>
                        </HeaderText>
                    </HeaderLeft>

                    <ActionsRow>
                        <Button
                            variant='featured'
                            category='squired'
                            onClick={handleToggle}
                        >
                            {data
                                ? <Edit color={colors.neutralLight} size={16} />
                                : <PlusSquare color={colors.neutralLight} size={16} />
                            }
                            <Text
                                color={colors.neutralLight}
                                paddingBottom={0}
                                marginLeft={'6px'}
                            >
                                {data ? 'Editar certificado' : 'Configurar certificado'}
                            </Text>
                        </Button>
                    </ActionsRow>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : data ? (
                    <ConfiguredCard>
                        <ConfiguredIcon>
                            <CheckCircle size={22} color={colors.success} />
                        </ConfiguredIcon>
                        <ConfiguredInfo>
                            <Text type='bold' color={colors.nightDark} paddingBottom={0} name='small'>
                                Certificado configurado
                            </Text>
                            <Text color={colors.night} paddingBottom={0} name='small'>
                                O certificado está pronto para ser emitido aos participantes.
                            </Text>
                        </ConfiguredInfo>
                    </ConfiguredCard>
                ) : (
                    <EmptyState>
                        <EmptyIcon>
                            <Award size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhum certificado configurado ainda.
                        </Text>
                        <EmptyHint>
                            Configure um certificado para valorizar a participação no seu evento.
                        </EmptyHint>
                    </EmptyState>
                )}
            </SectionCard>

            {modalCertificate && (
                <CertificateForm
                    visible={!!modalCertificate}
                    setVisible={setModalCertificate}
                    edit={modalCertificate?.item || false}
                    onSuccess={handleSuccess}
                />
            )}
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.success};
    background: #E8F5E9;
    padding: 3px 10px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const ConfiguredCard = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    background: #E8F5E908;
    border: 1px solid ${colors.success}33;
    border-radius: 10px;
`;

const ConfiguredIcon = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: #E8F5E9;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ConfiguredInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.night}0A;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;
